import { useState } from 'react'
import { useForm, Controller, type FieldErrors } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { createJobSchema, type CreateJobInput } from '@ats/shared'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/toaster'

const employmentTypes = [
  { value: 'full-time', label: 'Full-time' },
  { value: 'part-time', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'internship', label: 'Internship' },
]

const experienceLevels = [
  { value: 'entry', label: 'Entry Level' },
  { value: 'mid', label: 'Mid Level' },
  { value: 'senior', label: 'Senior' },
  { value: 'lead', label: 'Lead' },
  { value: 'executive', label: 'Executive' },
]

const remotePolicies = [
  { value: 'onsite', label: 'On-site' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'remote', label: 'Remote' },
]

const salaryPeriods = [
  { value: 'yearly', label: 'Per Year' },
  { value: 'monthly', label: 'Per Month' },
  { value: 'hourly', label: 'Per Hour' },
]

const currencies = [
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'GBP', label: 'GBP (£)' },
  { value: 'CAD', label: 'CAD ($)' },
  { value: 'AUD', label: 'AUD ($)' },
]

interface JobFormProps {
  initialData?: Partial<CreateJobInput>
  onSubmit: (data: CreateJobInput) => Promise<void>
  isLoading?: boolean
  submitLabel?: string
}

export function JobForm({
  initialData,
  onSubmit,
  isLoading,
  submitLabel = 'Create Job',
}: JobFormProps) {
  const [activeTab, setActiveTab] = useState('details')
  const [showNewDepartment, setShowNewDepartment] = useState(false)
  const [showNewLocation, setShowNewLocation] = useState(false)
  const [newDeptName, setNewDeptName] = useState('')
  const [newLocName, setNewLocName] = useState('')
  const queryClient = useQueryClient()

  // Fetch departments, locations, and users
  const { data: departments } = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<any[]>('/departments'),
  })

  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.get<any[]>('/locations'),
  })

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<any[]>('/users'),
  })

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateJobInput>({
    resolver: zodResolver(createJobSchema),
    defaultValues: {
      title: '',
      description: '',
      requirements: '',
      benefits: '',
      employmentType: 'full-time',
      remotePolicy: 'onsite',
      salaryCurrency: 'USD',
      salaryPeriod: 'yearly',
      showSalary: false,
      ...initialData,
    },
  })

  const showSalary = watch('showSalary')

  // Inline creation mutations
  const createDepartment = useMutation({
    mutationFn: (name: string) => api.post<any>('/departments', { name }),
    onSuccess: (dept) => {
      queryClient.invalidateQueries({ queryKey: ['departments'] })
      setValue('departmentId', dept.id)
      setShowNewDepartment(false)
      setNewDeptName('')
      toast({ title: `Department "${dept.name}" created`, variant: 'success' })
    },
    onError: () => {
      toast({ title: 'Failed to create department', variant: 'destructive' })
    },
  })

  const createLocation = useMutation({
    mutationFn: (name: string) => api.post<any>('/locations', { name }),
    onSuccess: (loc) => {
      queryClient.invalidateQueries({ queryKey: ['locations'] })
      setValue('locationId', loc.id)
      setShowNewLocation(false)
      setNewLocName('')
      toast({ title: `Location "${loc.name}" created`, variant: 'success' })
    },
    onError: () => {
      toast({ title: 'Failed to create location', variant: 'destructive' })
    },
  })

  // Switch to the tab with the first validation error
  const onValidationError = (fieldErrors: FieldErrors<CreateJobInput>) => {
    const detailsFields = ['title', 'description', 'departmentId', 'locationId', 'employmentType', 'remotePolicy', 'experienceLevel', 'hiringManagerId']
    const requirementsFields = ['requirements', 'benefits']
    const compensationFields = ['salaryMin', 'salaryMax', 'salaryCurrency', 'salaryPeriod', 'showSalary']

    for (const field of Object.keys(fieldErrors)) {
      if (detailsFields.includes(field)) { setActiveTab('details'); return }
      if (requirementsFields.includes(field)) { setActiveTab('requirements'); return }
      if (compensationFields.includes(field)) { setActiveTab('compensation'); return }
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit, onValidationError)} className="h-full flex flex-col">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 min-h-0 flex flex-col">
          <TabsList className="flex-shrink-0 grid w-full grid-cols-3">
            <TabsTrigger value="details">Job Details</TabsTrigger>
            <TabsTrigger value="requirements">Requirements</TabsTrigger>
            <TabsTrigger value="compensation">Compensation</TabsTrigger>
          </TabsList>

          {/* Job Details Tab */}
          <TabsContent value="details" className="flex-1 min-h-0 overflow-y-auto space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Title */}
                <div className="space-y-2">
                  <Label htmlFor="title">Job Title *</Label>
                  <Input
                    id="title"
                    placeholder="e.g. Senior Software Engineer"
                    error={!!errors.title}
                    {...register('title')}
                  />
                  {errors.title && (
                    <p className="text-sm text-destructive">{errors.title.message}</p>
                  )}
                </div>

                {/* Department & Location */}
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="job-department">Department</Label>
                    <Controller
                      name="departmentId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          value={field.value || ''}
                          onValueChange={(v) => field.onChange(v || undefined)}
                        >
                          <SelectTrigger id="job-department">
                            <SelectValue placeholder="Select department" />
                          </SelectTrigger>
                          <SelectContent>
                            {(departments || []).map((dept: any) => (
                              <SelectItem key={dept.id} value={dept.id}>
                                {dept.name}
                              </SelectItem>
                            ))}
                            <div
                              className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 px-2 text-sm text-primary hover:bg-accent outline-none border-t border-border mt-1 pt-2"
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setShowNewDepartment(true)
                              }}
                              onPointerDown={(e) => e.preventDefault()}
                            >
                              <Plus className="h-3.5 w-3.5 mr-2" />
                              Add new department
                            </div>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="job-location">Location</Label>
                    <Controller
                      name="locationId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          value={field.value || ''}
                          onValueChange={(v) => field.onChange(v || undefined)}
                        >
                          <SelectTrigger id="job-location">
                            <SelectValue placeholder="Select location" />
                          </SelectTrigger>
                          <SelectContent>
                            {(locations || []).map((loc: any) => (
                              <SelectItem key={loc.id} value={loc.id}>
                                {loc.name}
                              </SelectItem>
                            ))}
                            <div
                              className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 px-2 text-sm text-primary hover:bg-accent outline-none border-t border-border mt-1 pt-2"
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setShowNewLocation(true)
                              }}
                              onPointerDown={(e) => e.preventDefault()}
                            >
                              <Plus className="h-3.5 w-3.5 mr-2" />
                              Add new location
                            </div>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>

                {/* Employment Type & Remote Policy */}
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="job-employment-type">Employment Type</Label>
                    <Controller
                      name="employmentType"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger id="job-employment-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {employmentTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="job-remote-policy">Remote Policy</Label>
                    <Controller
                      name="remotePolicy"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger id="job-remote-policy">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {remotePolicies.map((policy) => (
                              <SelectItem key={policy.value} value={policy.value}>
                                {policy.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>

                {/* Experience Level & Hiring Manager */}
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="job-experience">Experience Level</Label>
                    <Controller
                      name="experienceLevel"
                      control={control}
                      render={({ field }) => (
                        <Select
                          value={field.value || ''}
                          onValueChange={(v) => field.onChange(v || undefined)}
                        >
                          <SelectTrigger id="job-experience">
                            <SelectValue placeholder="Select level" />
                          </SelectTrigger>
                          <SelectContent>
                            {experienceLevels.map((level) => (
                              <SelectItem key={level.value} value={level.value}>
                                {level.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="job-hiring-mgr">Hiring Manager</Label>
                    <Controller
                      name="hiringManagerId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          value={field.value || ''}
                          onValueChange={(v) => field.onChange(v || undefined)}
                        >
                          <SelectTrigger id="job-hiring-mgr">
                            <SelectValue placeholder="Select hiring manager" />
                          </SelectTrigger>
                          <SelectContent>
                            {(users || []).length === 0 ? (
                              <div className="p-2 text-sm text-muted-foreground text-center">
                                No team members yet
                              </div>
                            ) : (users || []).map((user: any) => (
                              <SelectItem key={user.id} value={user.id}>
                                {user.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="job-description">Job Description *</Label>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <RichTextEditor
                        id="job-description"
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Describe the role, responsibilities, and what makes this opportunity exciting..."
                        error={!!errors.description}
                      />
                    )}
                  />
                  {errors.description && (
                    <p className="text-sm text-destructive">
                      {errors.description.message}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Requirements Tab */}
          <TabsContent value="requirements" className="flex-1 min-h-0 overflow-y-auto space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Requirements & Benefits</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="job-requirements">Requirements</Label>
                  <Controller
                    name="requirements"
                    control={control}
                    render={({ field }) => (
                      <RichTextEditor
                        id="job-requirements"
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="List the skills, qualifications, and experience required..."
                      />
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="job-benefits">Benefits</Label>
                  <Controller
                    name="benefits"
                    control={control}
                    render={({ field }) => (
                      <RichTextEditor
                        id="job-benefits"
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="Describe the benefits, perks, and what you offer..."
                      />
                    )}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compensation Tab */}
          <TabsContent value="compensation" className="flex-1 min-h-0 overflow-y-auto space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Salary Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Show Salary Toggle */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="job-show-salary">Display Salary</Label>
                    <p className="text-sm text-muted-foreground">
                      Show salary range on the job posting
                    </p>
                  </div>
                  <Controller
                    name="showSalary"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        id="job-show-salary"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                </div>

                {/* Salary Range */}
                <div className="grid gap-4 md:grid-cols-4">
                  <div className="space-y-2">
                    <Label htmlFor="job-currency">Currency</Label>
                    <Controller
                      name="salaryCurrency"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger id="job-currency">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {currencies.map((currency) => (
                              <SelectItem key={currency.value} value={currency.value}>
                                {currency.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="job-salary-min">Minimum</Label>
                    <Input
                      id="job-salary-min"
                      type="number"
                      placeholder="50000"
                      {...register('salaryMin', {
                        setValueAs: (v: string) => {
                          if (v === '' || v === undefined || v === null) return undefined
                          const n = Number(v)
                          return isNaN(n) ? undefined : n
                        },
                      })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="job-salary-max">Maximum</Label>
                    <Input
                      id="job-salary-max"
                      type="number"
                      placeholder="80000"
                      {...register('salaryMax', {
                        setValueAs: (v: string) => {
                          if (v === '' || v === undefined || v === null) return undefined
                          const n = Number(v)
                          return isNaN(n) ? undefined : n
                        },
                      })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="job-salary-period">Period</Label>
                    <Controller
                      name="salaryPeriod"
                      control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger id="job-salary-period">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {salaryPeriods.map((period) => (
                              <SelectItem key={period.value} value={period.value}>
                                {period.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>

                {errors.salaryMin && (
                  <p className="text-sm text-destructive">{errors.salaryMin.message}</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Submit Button */}
        <div className="flex-shrink-0 flex justify-end gap-4 pt-3 border-t mt-3">
          <Button type="button" variant="outline">
            Save as Draft
          </Button>
          <Button type="submit" loading={isLoading}>
            {submitLabel}
          </Button>
        </div>
      </form>

      {/* Quick-Add Department Dialog */}
      <Dialog open={showNewDepartment} onOpenChange={setShowNewDepartment}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Department</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="new-dept-name">Department Name</Label>
              <Input
                id="new-dept-name"
                placeholder="e.g. Engineering, Marketing, Sales"
                value={newDeptName}
                onChange={(e) => setNewDeptName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newDeptName.trim()) {
                    e.preventDefault()
                    createDepartment.mutate(newDeptName.trim())
                  }
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowNewDepartment(false); setNewDeptName('') }}>
              Cancel
            </Button>
            <Button
              onClick={() => createDepartment.mutate(newDeptName.trim())}
              disabled={!newDeptName.trim() || createDepartment.isPending}
              loading={createDepartment.isPending}
            >
              Add Department
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick-Add Location Dialog */}
      <Dialog open={showNewLocation} onOpenChange={setShowNewLocation}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Location</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="new-loc-name">Location Name</Label>
              <Input
                id="new-loc-name"
                placeholder="e.g. New York, San Francisco, London"
                value={newLocName}
                onChange={(e) => setNewLocName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newLocName.trim()) {
                    e.preventDefault()
                    createLocation.mutate(newLocName.trim())
                  }
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowNewLocation(false); setNewLocName('') }}>
              Cancel
            </Button>
            <Button
              onClick={() => createLocation.mutate(newLocName.trim())}
              disabled={!newLocName.trim() || createLocation.isPending}
              loading={createLocation.isPending}
            >
              Add Location
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
